import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, UserX, UserCheck, Crown, RotateCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, SectionTitle, StatusBadge, ConfirmDialog, Field, FormError, SubmitButton } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { fmtBytes, fmtDate, fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

async function run(fn, ok, after) {
  try {
    await fn();
    toast.success(ok);
    after?.();
    return true;
  } catch (e) {
    toast.error(errMsg(e));
    return false;
  }
}

function MemberRow({ m, gid, onChanged, onMakeManager }) {
  const base = `/admin/garages/${gid}/members/${m.membership_id}`;
  const inactive = m.status === "inactive";
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-3 text-sm last:border-0" data-testid={`admin-member-${m.email}`}>
      <span className="min-w-0 flex-1">{m.name} <span className="text-slate-500">· {m.email}</span></span>
      <StatusBadge status={m.role} />
      {inactive && <StatusBadge status="inactive" testId={`admin-member-inactive-${m.email}`} />}
      <Button variant="ghost" size="sm" title={t("p7.access.resetPw")} onClick={() => run(() => api.post(`/admin/users/${m.user_id}/password-reset`), t("p7.access.resetSent"))} data-testid={`admin-reset-pw-${m.email}`}><KeyRound className="h-4 w-4" /></Button>
      {m.role === "mechanic" && !inactive && <Button variant="ghost" size="sm" title={t("p7.access.makeManager")} onClick={() => onMakeManager(m)} data-testid={`admin-make-manager-${m.email}`}><Crown className="h-4 w-4" /></Button>}
      <Button variant="ghost" size="sm" className={inactive ? "text-emerald-700" : "text-red-600"} title={inactive ? t("p7.access.reactivate") : t("p7.access.deactivate")}
        onClick={() => run(() => api.post(`${base}/status`, { status: inactive ? "active" : "inactive" }), inactive ? t("p7.access.reactivated") : t("p7.access.deactivated"), onChanged)}
        data-testid={`admin-toggle-member-${m.email}`}>{inactive ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}</Button>
    </div>
  );
}

export function TeamAccessCard({ detail, onChanged }) {
  const [target, setTarget] = useState(null);
  const gid = detail.garage.id;
  const makeManager = async () => {
    if (await run(() => api.post(`/admin/garages/${gid}/change-manager`, { membership_id: target.membership_id, demote_previous: true }), t("p7.access.managerChanged"), onChanged)) setTarget(null);
  };
  return (
    <Card testId="garage-team-card">
      <SectionTitle hint={t("admin.teamHint")}>{t("admin.team")}</SectionTitle>
      {detail.members.length === 0 ? <p className="text-sm text-slate-500">{t("admin.noMembers")}</p> : detail.members.map((m) => <MemberRow key={m.membership_id} m={m} gid={gid} onChanged={onChanged} onMakeManager={setTarget} />)}
      <p className="mt-4 text-sm text-slate-600">{t("admin.storageUsed", { used: fmtBytes(detail.storage_bytes), quota: detail.plan?.photo_quota_mb ?? "—" })}</p>
      <ConfirmDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)} title={t("p7.access.changeTitle", { name: target?.name })} description={t("p7.access.changeText")} confirmLabel={t("p7.access.makeManager")} onConfirm={makeManager} testId="change-manager-dialog" />
    </Card>
  );
}

export function InviteList({ detail, onChanged }) {
  return (
    <div className="mt-6 space-y-2" data-testid="admin-invites-list">
      {detail.invites.length === 0 && <p className="text-sm text-slate-500">{t("admin.noInvites")}</p>}
      {detail.invites.map((i) => (
        <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-3 text-sm">
          <span className="min-w-0 truncate">{i.email} · {t(`status.${i.role}`)}</span>
          <span className="flex items-center gap-2 text-xs text-slate-500">{fmtDateTime(i.created_at)} <StatusBadge status={i.status} />
            {i.status === "pending" && <Button variant="outline" size="sm" onClick={() => run(() => api.post(`/admin/garages/${detail.garage.id}/invites/${i.id}/resend`), t("p7.access.resent"), onChanged)} data-testid={`admin-resend-invite-${i.id}`}><RotateCw className="mr-1 h-3.5 w-3.5" />{t("p7.access.resend")}</Button>}
          </span>
        </div>
      ))}
    </div>
  );
}

export function ClearDemoCard({ detail, onChanged }) {
  const g = detail.garage;
  const [slug, setSlug] = useState("");
  const clear = (e) => { e.preventDefault(); run(() => api.post(`/admin/garages/${g.id}/clear-demo-data`, { confirm_slug: slug }), t("b8.demo.cleared"), () => { setSlug(""); onChanged(); }); };
  return (
    <Card testId="clear-demo-card">
      <SectionTitle hint={t("b8.demo.clearHint")}>{t("b8.demo.clearTitle")}</SectionTitle>
      <form onSubmit={clear} className="space-y-3">
        <Field label={t("p7.admin.typeSlug", { slug: g.slug })} htmlFor="cd-slug"><Input id="cd-slug" autoComplete="off" value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="clear-demo-slug-input" /></Field>
        <Button type="submit" variant="outline" className="text-red-700" disabled={slug !== g.slug} data-testid="clear-demo-button">{t("b8.demo.clearBtn")}</Button>
      </form>
    </Card>
  );
}

export function StripeNotice({ detail, onDone }) {
  const sub = detail.subscription;
  const act = (action, ok) => run(() => api.post(`/admin/garages/${detail.garage.id}/stripe`, { action }), ok, onDone);
  return (
    <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="stripe-live-notice">
      <p>{t("p7.admin.stripeWarn", { status: sub.stripe_status })}</p>
      <div className="flex flex-wrap gap-2">
        {sub.cancel_at_period_end
          ? <Button type="button" size="sm" variant="outline" onClick={() => act("resume", t("p7.billing.resumed"))} data-testid="admin-stripe-resume">{t("p7.billing.resume")}</Button>
          : <Button type="button" size="sm" variant="outline" onClick={() => act("cancel_at_period_end", t("p7.billing.cancelled_ok"))} data-testid="admin-stripe-cancel-period">{t("p7.admin.stripeCancelPeriod")}</Button>}
        <Button type="button" size="sm" variant="outline" className="text-red-700" onClick={() => act("cancel_now", t("p7.admin.stripeCancelledNow"))} data-testid="admin-stripe-cancel-now">{t("p7.admin.stripeCancelNow")}</Button>
      </div>
    </div>
  );
}

export function TestFlagCard({ detail, onChanged }) {
  const g = detail.garage;
  const [f, setF] = useState({ is_test: g.is_test, test_note: g.test_note || "", confirm_slug: "" });
  const needsConfirm = f.is_test && !g.is_test;
  const save = (e) => { e.preventDefault(); run(() => api.patch(`/admin/garages/${g.id}/flags`, f), t("p7.admin.flagSaved"), onChanged); };
  return (
    <Card testId="test-flag-card">
      <SectionTitle hint={t("p7.admin.testFlagHint")}>{t("p7.admin.testFlag")}</SectionTitle>
      {g.is_protected ? <p className="text-sm font-medium text-slate-700" data-testid="test-flag-protected">{t("p7.admin.protected")}</p> : (
        <form onSubmit={save} className="space-y-4">
          <label className="flex items-center justify-between text-sm"><span>{t("p7.admin.testFlag")}</span><Switch checked={f.is_test} onCheckedChange={(v) => setF({ ...f, is_test: v, confirm_slug: "" })} data-testid="test-flag-switch" /></label>
          <Field label={t("p7.admin.testNote")} htmlFor="tf-note"><Input id="tf-note" value={f.test_note} onChange={(e) => setF({ ...f, test_note: e.target.value })} data-testid="test-flag-note-input" /></Field>
          {needsConfirm && (
            <Field label={t("p7.admin.flagConfirm", { slug: g.slug })} htmlFor="tf-confirm">
              <Input id="tf-confirm" autoComplete="off" value={f.confirm_slug} onChange={(e) => setF({ ...f, confirm_slug: e.target.value })} data-testid="test-flag-confirm-input" />
            </Field>
          )}
          <Button type="submit" variant="outline" disabled={needsConfirm && f.confirm_slug !== g.slug} data-testid="test-flag-save-button">{t("p7.admin.saveFlag")}</Button>
        </form>
      )}
    </Card>
  );
}

export function ManualBillingCard({ detail, onChanged }) {
  const [interval, setInterval] = useState("monthly");
  const sub = detail.subscription;
  return (
    <Card testId="manual-billing-card">
      <SectionTitle hint={t("p7.admin.manualHint")}>{t("p7.admin.manualTitle")}</SectionTitle>
      <dl className="mb-4 space-y-1 text-sm">
        <div className="flex justify-between"><dt className="text-slate-500">{t("p7.admin.billingMode")}</dt><dd className="font-semibold" data-testid="admin-billing-mode">{sub.billing_mode === "stripe" ? t("p7.billing.stripe") : t("p7.billing.manual")}</dd></div>
        {sub.paid_until && <div className="flex justify-between"><dt className="text-slate-500">Paid until</dt><dd className="font-semibold" data-testid="admin-paid-until">{fmtDate(sub.paid_until)}</dd></div>}
        {sub.stripe_subscription_id && <div className="flex justify-between"><dt className="text-slate-500">{t("p7.admin.stripeLinks")}</dt><dd className="font-mono text-xs">{sub.stripe_subscription_id} · {sub.stripe_status}</dd></div>}
        {sub.awaiting_card && <div className="text-xs font-semibold text-red-600">{t("p7.admin.awaitingCard")}</div>}
      </dl>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={interval} onValueChange={setInterval}>
          <SelectTrigger className="w-36" data-testid="mark-paid-interval-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="monthly">{t("p7.pricing.monthly")}</SelectItem><SelectItem value="yearly">{t("p7.pricing.yearly")}</SelectItem></SelectContent>
        </Select>
        <Button onClick={() => run(() => api.post(`/admin/garages/${detail.garage.id}/mark-paid`, { interval }), t("p7.admin.markedPaid"), onChanged)} data-testid="mark-paid-button">{t("p7.admin.markPaid")}</Button>
      </div>
    </Card>
  );
}

export function DangerZoneCard({ detail }) {
  const g = detail.garage;
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post(`/admin/garages/${g.id}/delete-permanently`, { confirm_slug: slug });
      toast.success(t("p7.admin.deleted"));
      navigate("/admin/garages", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="border-red-200" testId="danger-zone-card">
      <SectionTitle hint={t("p7.admin.dangerHint")}><span className="text-red-700">{t("p7.admin.danger")}</span></SectionTitle>
      {!g.can_delete ? <p className="text-sm text-slate-600" data-testid="danger-zone-locked">{t(g.is_protected ? "p7.admin.protected" : "p7.admin.dangerLocked")}</p> : (
        <form onSubmit={submit} className="space-y-3">
          <FormError message={error} />
          <Field label={t("p7.admin.typeSlug", { slug: g.slug })} htmlFor="dz-slug"><Input id="dz-slug" value={slug} onChange={(e) => setSlug(e.target.value)} autoComplete="off" data-testid="delete-confirm-slug-input" /></Field>
          <SubmitButton busy={busy} disabled={slug !== g.slug} className="bg-red-600 hover:bg-red-700" data-testid="delete-garage-button"><Trash2 className="mr-2 h-4 w-4" />{t("p7.admin.deleteBtn")}</SubmitButton>
        </form>
      )}
    </Card>
  );
}
